const cleanNotificationText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const formatNotificationCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "";

  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNotificationDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return cleanNotificationText(value);

  return new Intl.DateTimeFormat("es-CO", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const getNotificationAccentByLevel = (level = "info") => {
  const normalizedLevel = String(level || "info").toLowerCase();

  if (normalizedLevel === "success") {
    return {
      accentColor: "#1f9d67",
      softColor: "rgba(31, 157, 103, 0.14)",
      iconName: "check-circle",
    };
  }

  if (normalizedLevel === "warning") {
    return {
      accentColor: "#d97706",
      softColor: "rgba(217, 119, 6, 0.14)",
      iconName: "alert-circle",
    };
  }

  if (normalizedLevel === "error") {
    return {
      accentColor: "#dc4c64",
      softColor: "rgba(220, 76, 100, 0.14)",
      iconName: "alert-triangle",
    };
  }

  return {
    accentColor: "#2563eb",
    softColor: "rgba(37, 99, 235, 0.14)",
    iconName: "bell",
  };
};

const resolveTicketId = (notification = {}) => {
  const candidates = [
    notification?.metadata?.ticketId,
    notification?.action?.state?.focusTicketId,
  ];

  return (
    candidates
      .map((value) => Number(value))
      .find((value) => Number.isFinite(value) && value > 0) || 0
  );
};

const buildEscalatedTicketPresentation = (notification = {}) => {
  const metadata = notification?.metadata || {};
  const ticketId = resolveTicketId(notification);
  const ticketTitle = cleanNotificationText(metadata?.ticketTitle);
  const targetGroupName = cleanNotificationText(metadata?.grupoDestinoNombre);
  const statusName = cleanNotificationText(metadata?.estadoNombre);
  const reason = cleanNotificationText(metadata?.motivo);

  return {
    iconName: "clipboard",
    accentColor: "#ef6a5b",
    softColor: "rgba(239, 106, 91, 0.14)",
    badgeLabel: "Ticket asignado",
    title: ticketId
      ? `Se te asignó el ticket #${ticketId}`
      : "Se te asignó un ticket",
    message: ticketTitle
      ? `"${ticketTitle}" llegó a tu área para que continúes la atención.`
      : "Tienes un caso nuevo para revisar y continuar con la atención.",
    metaLine: [targetGroupName && `Área: ${targetGroupName}`, statusName && `Estado: ${statusName}`]
      .filter(Boolean)
      .join(" · "),
    helperText: reason
      ? `Motivo informado: ${reason}`
      : "Ábrelo para revisar los detalles y continuar con la atención.",
    actionLabel:
      cleanNotificationText(notification?.action?.label) || "Abrir ticket",
  };
};

const buildBillingInvoiceRequiredPresentation = (notification = {}) => {
  const metadata = notification?.metadata || {};
  const contratoId = Number(metadata?.contratoId || 0);
  const cuotaNumero = cleanNotificationText(metadata?.numeroCuota || "");
  const clienteNombre = cleanNotificationText(metadata?.clienteNombre);
  const productoNombre = cleanNotificationText(metadata?.productoNombre);
  const saldoPendiente = formatNotificationCurrency(metadata?.saldoPendiente);
  const fechaVencimiento = formatNotificationDate(metadata?.fechaVencimiento);
  const timingLabel = cleanNotificationText(metadata?.timingLabel);

  return {
    iconName: "clipboard",
    accentColor: "#2563eb",
    softColor: "rgba(37, 99, 235, 0.14)",
    badgeLabel: "Facturación",
    title: contratoId
      ? `Debes facturar el contrato #${contratoId}`
      : "Tienes una cuota pendiente por facturar",
    message: clienteNombre
      ? `${clienteNombre} tiene una cuota pendiente de facturación.`
      : "Hay una cuota que todavía no tiene factura asociada.",
    metaLine: [
      cuotaNumero && `Cuota ${cuotaNumero}`,
      productoNombre && productoNombre,
      saldoPendiente && saldoPendiente,
      fechaVencimiento && `Vence ${fechaVencimiento}`,
    ]
      .filter(Boolean)
      .join(" · "),
    helperText:
      timingLabel
        ? `Genera la factura cuanto antes: ${timingLabel}.`
        : "Genera la factura para evitar atrasos en el cobro.",
    actionLabel:
      cleanNotificationText(notification?.action?.label) || "Abrir facturación",
  };
};

const buildBillingPaymentDuePresentation = (notification = {}) => {
  const metadata = notification?.metadata || {};
  const contratoId = Number(metadata?.contratoId || 0);
  const cuotaNumero = cleanNotificationText(metadata?.numeroCuota || "");
  const clienteNombre = cleanNotificationText(metadata?.clienteNombre);
  const productoNombre = cleanNotificationText(metadata?.productoNombre);
  const saldoPendiente = formatNotificationCurrency(metadata?.saldoPendiente);
  const fechaVencimiento = formatNotificationDate(metadata?.fechaVencimiento);
  const timingLabel = cleanNotificationText(metadata?.timingLabel);

  return {
    iconName: "alert-circle",
    accentColor: "#d97706",
    softColor: "rgba(217, 119, 6, 0.14)",
    badgeLabel: "Cobro próximo",
    title: contratoId
      ? `La cuota del contrato #${contratoId} vence pronto`
      : "Tienes un pago próximo a vencer",
    message: clienteNombre
      ? `${clienteNombre} tiene un saldo pendiente por revisar.`
      : "Hay una cuota próxima a vencer con pago pendiente.",
    metaLine: [
      cuotaNumero && `Cuota ${cuotaNumero}`,
      productoNombre && productoNombre,
      saldoPendiente && saldoPendiente,
      fechaVencimiento && `Vence ${fechaVencimiento}`,
    ]
      .filter(Boolean)
      .join(" · "),
    helperText:
      timingLabel
        ? `Haz seguimiento al pago: ${timingLabel}.`
        : "Haz seguimiento para confirmar el pago a tiempo.",
    actionLabel:
      cleanNotificationText(notification?.action?.label) || "Abrir facturación",
  };
};

const buildBillingOverduePresentation = (notification = {}) => {
  const metadata = notification?.metadata || {};
  const contratoId = Number(metadata?.contratoId || 0);
  const cuotaNumero = cleanNotificationText(metadata?.numeroCuota || "");
  const clienteNombre = cleanNotificationText(metadata?.clienteNombre);
  const productoNombre = cleanNotificationText(metadata?.productoNombre);
  const saldoPendiente = formatNotificationCurrency(metadata?.saldoPendiente);
  const fechaVencimiento = formatNotificationDate(metadata?.fechaVencimiento);
  const timingLabel = cleanNotificationText(metadata?.timingLabel);

  return {
    iconName: "alert-triangle",
    accentColor: "#dc2626",
    softColor: "rgba(220, 38, 38, 0.14)",
    badgeLabel: "Cuota en mora",
    title: contratoId
      ? `La cuota del contrato #${contratoId} está en mora`
      : "Tienes una cuota en mora",
    message: clienteNombre
      ? `${clienteNombre} registra una cuota vencida que necesita gestión inmediata.`
      : "Hay una cuota vencida pendiente por gestionar.",
    metaLine: [
      cuotaNumero && `Cuota ${cuotaNumero}`,
      productoNombre && productoNombre,
      saldoPendiente && saldoPendiente,
      fechaVencimiento && `Venció ${fechaVencimiento}`,
    ]
      .filter(Boolean)
      .join(" · "),
    helperText:
      timingLabel
        ? `Revisa el cobro: ${timingLabel}.`
        : "Revisa el cobro y valida si el cliente ya realizó el pago.",
    actionLabel:
      cleanNotificationText(notification?.action?.label) || "Abrir facturación",
  };
};

export const getNotificationPresentation = (notification = {}) => {
  const defaultAppearance = getNotificationAccentByLevel(notification?.level);
  const title = cleanNotificationText(notification?.title) || "Nueva notificación";
  const message = cleanNotificationText(notification?.message);
  const ticketId = resolveTicketId(notification);

  if (notification?.type === "ticket.escalated") {
    return buildEscalatedTicketPresentation(notification);
  }

  if (
    notification?.type === "facturacion.pendiente_facturar" ||
    notification?.type === "billing.invoice_required"
  ) {
    return buildBillingInvoiceRequiredPresentation(notification);
  }

  if (
    notification?.type === "facturacion.proximo_pago" ||
    notification?.type === "billing.payment_due"
  ) {
    return buildBillingPaymentDuePresentation(notification);
  }

  if (
    notification?.type === "facturacion.en_mora" ||
    notification?.type === "billing.overdue"
  ) {
    return buildBillingOverduePresentation(notification);
  }

  return {
    ...defaultAppearance,
    badgeLabel: cleanNotificationText(notification?.scope) === "soporte"
      ? "Soporte"
      : "Aviso",
    title,
    message:
      message || "Tienes una novedad pendiente por revisar.",
    metaLine: ticketId ? `Ticket #${ticketId}` : "",
    helperText:
      cleanNotificationText(notification?.action?.label)
        ? `${cleanNotificationText(notification?.action?.label)} desde esta notificación.`
        : "Revísala cuando tengas un momento.",
    actionLabel:
      cleanNotificationText(notification?.action?.label) || "Abrir",
  };
};
